import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MailOpen } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function MessagesPage({ onRead }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/messages");
      setItems(data);
      if (data.length && !selected) setSelected(data[0]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await api.patch(`/messages/${id}/read`);
      setItems((prev) => prev.map((m) => (m.id === id ? { ...m, status: "read" } : m)));
      if (selected?.id === id) setSelected({ ...selected, status: "read" });
      onRead?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="mono-label">INBOUND</div>
        <h2 className="text-2xl font-bold tracking-tight mt-1">Customer messages</h2>
        <p className="text-sm text-zinc-600">Questions from the Apex website contact form and the customer portal.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-zinc-200 bg-white max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="p-6 mono-label">Loading...</div>
          ) : items.length === 0 ? (
            <div className="p-6 mono-label text-center">Inbox empty</div>
          ) : items.map((m) => (
            <button
              key={m.id}
              onClick={() => setSelected(m)}
              data-testid={`msg-item-${m.id}`}
              className={`w-full text-left px-4 py-3 border-b border-zinc-100 flex items-start gap-3 ${
                selected?.id === m.id ? "bg-zinc-100" : "hover:bg-zinc-50"
              }`}
            >
              {m.status === "new" ? (
                <Mail className="h-4 w-4 text-[#0056B3] mt-0.5" />
              ) : (
                <MailOpen className="h-4 w-4 text-zinc-400 mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">{m.name}</span>
                  {m.status === "new" && <span className="mono-label !text-[#0056B3]">NEW</span>}
                </div>
                <div className="text-xs text-zinc-600 truncate">{m.subject}</div>
                <div className="mono-label !text-[10px] mt-0.5">
                  {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="md:col-span-2 border border-zinc-200 bg-white p-6 min-h-[400px]">
          {!selected ? (
            <div className="mono-label text-center py-16">Select a message</div>
          ) : (
            <div>
              <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
                <div>
                  <div className="mono-label">{new Date(selected.created_at).toLocaleString()}</div>
                  <h3 className="text-xl font-semibold mt-1">{selected.subject}</h3>
                  <div className="text-sm text-zinc-600 mt-1">
                    From <span className="font-medium text-zinc-950">{selected.name}</span>
                    {selected.company && <> · {selected.company}</>}
                    <> · <a href={`mailto:${selected.email}`} className="underline">{selected.email}</a></>
                    {selected.phone && <> · {selected.phone}</>}
                  </div>
                </div>
                {selected.status === "new" && (
                  <Button onClick={() => markRead(selected.id)} className="sharp bg-zinc-950 hover:bg-zinc-800" data-testid="msg-mark-read">
                    Mark read
                  </Button>
                )}
              </div>
              <div className="whitespace-pre-wrap text-sm mt-6 leading-relaxed">{selected.message}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
